import { LitElement, html, css, unsafeCSS, nothing } from 'lit';
import stylesTable from './catalog-item-table.scss?inline';
import stylesTile from './catalog-item-tile.scss?inline';
import { AVAILABLE_COUNT_PROP, CATALOG_VARIANT_TABLE, CATALOG_VARIANT_TILE, EXPANDED_ATTRIBUTE } from '../constants';
import { map } from 'lit/directives/map.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { classMap } from 'lit/directives/class-map.js';
import propertyArrayConverter from '@/helpers/propertyArrayConverter.js';
import { isLaptopMaxWidth } from '@/helpers';
import { cache } from 'lit/directives/cache.js';

export default class SotbitCatalogItem extends LitElement {
    static styles = [
        css`
            ${unsafeCSS(stylesTile)}
        `,
        css`
            ${unsafeCSS(stylesTable)}
        `,
    ];

    static properties = {
        hideImg: { type: Boolean, attribute: 'hide-img' },
        isOffers: { type: Boolean, attribute: 'is-offers' },
        isOffer: { type: Boolean, attribute: 'offer' },
        isToggleOffers: { type: Boolean, attribute: 'toggle-offers' },
        offersOpen: { type: Boolean, attribute: 'offers-open' },
        variant: { type: String },
        articleProp: { type: String, attribute: 'article-prop' },
        properties: {
            type: Array,
            converter: propertyArrayConverter,
        },
        tableProperties: {
            type: Array,
            converter: propertyArrayConverter,
            attribute: 'table-properties',
        },
        hasCounterSlot: { type: Boolean, state: true },
        hasOneClickSlot: { type: Boolean, state: true },
        hasPropertyListContent: { type: Boolean, state: true },
    };

    constructor() {
        super();

        this.hideImg = false;
        this.isOffers = false;
        this.isToggleOffers = false;
        this.variant = CATALOG_VARIANT_TILE;
        this.properties = [];
        this.tableProperties = [];
        this.articleProp = '';
        this._itemTileRef = createRef();
        this._itemTilePropertyListRef = createRef();
        this._tileCounterSlotRef = createRef();
        this._tileOneClickSlotRef = createRef();
        this._propertySlotRefs = new Map();
        this._isMouseOver = false;
        this.hasCounterSlot = false;
        this.hasOneClickSlot = false;
        this.hasPropertyListContent = false;
        this._adjustItemTilePosition = this._adjustItemTilePosition.bind(this);
        this._cleanUpItemTilePosition = this._cleanUpItemTilePosition.bind(this);
    }

    _handleItemMouseover() {
        this.setAttribute('hovered', '');
    }

    _handleItemMouseout() {
        this.removeAttribute('hovered');
    }

    _handleOpenOffers(e) {
        if (e.target.closest('[slot]') && e.target !== e.currentTarget) {
            return;
        }
        if (this.offersOpen) {
            this.removeAttribute('offers-open');
        } else {
            this.setAttribute('offers-open', '');
        }
    }

    _handleMouseover() {
        if (!this._isMouseOver) {
            this._isMouseOver = true;

            if (this.variant === CATALOG_VARIANT_TILE && !isLaptopMaxWidth()) {
                this._adjustItemTilePosition();
                this.setAttribute(EXPANDED_ATTRIBUTE, '');
            }
        }
    }

    _handleMouseout(event) {
        if (!this._isMouseOver) {
            return;
        }

        if (!event.relatedTarget || !this.contains(event.relatedTarget)) {
            this._isMouseOver = false;

            if (this.variant === CATALOG_VARIANT_TILE && !isLaptopMaxWidth()) {
                this._cleanUpItemTilePosition();
                this.removeAttribute(EXPANDED_ATTRIBUTE);
            }
        }
    }

    _adjustItemTilePosition() {
        if (!this._itemTileRef?.value || !this._itemTilePropertyListRef?.value) {
            return;
        }

        const itemNode = this._itemTileRef.value;
        const propertyListNode = this._itemTilePropertyListRef.value;
        const isVisible = (node) => node && node.offsetParent !== null && node.offsetHeight > 0;
        const counterNode = itemNode.querySelector(
            '.catalog-item-counter.has-counter, .catalog-item-counter.has-one-click'
        );
        const contentNodes = itemNode.querySelectorAll(
            '.catalog-item-available, .catalog-item-article, .catalog-item-name, .catalog-item-prices'
        );
        const itemTop = itemNode.getBoundingClientRect().top;
        let anchorBottom = 0;

        if (isVisible(counterNode)) {
            anchorBottom = counterNode.getBoundingClientRect().bottom;
        } else {
            contentNodes.forEach((node) => {
                if (!isVisible(node)) {
                    return;
                }

                const bottom = node.getBoundingClientRect().bottom;
                if (bottom > anchorBottom) {
                    anchorBottom = bottom;
                }
            });
        }

        itemNode.style.setProperty('--ci-property-list-height', `${propertyListNode.offsetHeight}px`);

        if (!anchorBottom) {
            return;
        }

        itemNode.style.setProperty('--ci-property-list-top', `${anchorBottom - itemTop}px`);
    }

    _cleanUpItemTilePosition() {
        if (!this._itemTileRef?.value) {
            return;
        }

        this._itemTileRef.value.removeAttribute('style');
    }

    _hasAssignedSlotContent(slot) {
        return (slot?.assignedElements({ flatten: true }) || []).some((el) => !el.hasAttribute('hidden'));
    }

    /**
     * Whether a property-list slot has assigned content.
     * @param {HTMLSlotElement} slot
     * @returns {boolean}
     */
    _hasAssignedPropertyListSlotContent(slot) {
        return (slot?.assignedElements({ flatten: true }) || []).some(
            (el) => !el.hasAttribute('hidden') && !el.matches(':empty')
        );
    }

    _handleTileActionSlotChange() {
        this.hasCounterSlot = this._hasAssignedSlotContent(this._tileCounterSlotRef?.value);
        this.hasOneClickSlot = this._hasAssignedSlotContent(this._tileOneClickSlotRef?.value);
    }

    firstUpdated(changedProperties) {
        super.firstUpdated(changedProperties);
        this._handleTileActionSlotChange();
        this._handlePropertyListSlotChange();
    }

    _setPropertySlotRef(property) {
        return (el) => {
            if (el) {
                this._propertySlotRefs.set(property, el);
            } else {
                this._propertySlotRefs.delete(property);
            }
        };
    }

    _handlePropertyListSlotChange() {
        this.hasPropertyListContent = [...this._propertySlotRefs.values()].some((slot) =>
            this._hasAssignedPropertyListSlotContent(slot)
        );
    }

    connectedCallback() {
        super.connectedCallback();

        this.addEventListener('mouseover', this._handleMouseover);
        this.addEventListener('mouseout', this._handleMouseout);

        if (isLaptopMaxWidth()) {
            this.variant = CATALOG_VARIANT_TILE;
            this.setAttribute('variant', CATALOG_VARIANT_TILE);
        }
    }

    templateTable() {
        return html` <slot></slot>
            <div class="catalog-item" @mouseover="${this._handleItemMouseover}" @mouseout="${this._handleItemMouseout}">
                ${this.isToggleOffers
                    ? html`
                          <sotbit-catalog-section-table-cell
                              left-rounded
                              variant="catalog-item"
                              settings
                              class="column"
                          >
                              <div slot="content">
                                  <div class="offers-toggle">
                                      ${this.isOffers
                                          ? html` <div @click="${this._handleOpenOffers}">
                                                ${this.isOffers
                                                    ? html`<sotbit-icon
                                                          @click="${this.isOffers ? this._handleOpenOffers : nothing}"
                                                          icon="chevron-right"
                                                          size="m"
                                                          background
                                                          variant="secondary"
                                                          pointer
                                                      ></sotbit-icon>`
                                                    : nothing}
                                            </div>`
                                          : ''}
                                  </div>
                              </div>
                          </sotbit-catalog-section-table-cell>
                      `
                    : html`
                          <sotbit-catalog-section-table-cell settings class="column">
                              <div slot="content"></div>
                          </sotbit-catalog-section-table-cell>
                      `}

                <sotbit-catalog-section-table-cell
                    ?left-rounded="${this.isToggleOffers !== true}"
                    ?left-padding-0="${this.isToggleOffers}"
                    right-border
                    variant="catalog-item"
                    product-name
                    class="column column--name"
                >
                    <div slot="content">
                        <div class="info">
                            ${this.hideImg !== true
                                ? html` <div class="img">
                                      <slot name="img"></slot>
                                  </div>`
                                : nothing}
                            <div class="name-wrapper" id="name">
                                <div class="tags">
                                    <slot name="tag"></slot>
                                </div>
                                <div class="name">
                                    <slot name="name"></slot>
                                </div>
                            </div>
                        </div>
                    </div>
                </sotbit-catalog-section-table-cell>

                ${map(
                    this.tableProperties,
                    (property) => html`
                        <sotbit-catalog-section-table-cell
                            @click="${this.isOffers ? this._handleOpenOffers : nothing}"
                            class="column"
                            variant="catalog-item"
                            part="column-property"
                        >
                            <div slot="content">
                                <slot ?data-article="${property === this.articleProp}" name="${property}"></slot>
                            </div>
                        </sotbit-catalog-section-table-cell>
                    `
                )}

                <sotbit-catalog-section-table-cell
                    left-border
                    right-rounded
                    right-sticky
                    class="column column--counter"
                    variant="catalog-item"
                >
                    <div slot="content" class="counter-wrapper">
                        <div class="actions">
                            <div class="counter">
                                <slot name="counter"></slot>
                            </div>
                            <div class="compare">
                                <slot name="compare"></slot>
                            </div>
                        </div>
                        <slot name="one-click"></slot>
                        <slot name="min-price"></slot>
                    </div>
                </sotbit-catalog-section-table-cell>
            </div>
            ${this.isOffers
                ? html`<div class="catalog-item-offers">
                      <div class="outline"></div>
                      <slot name="offers"></slot>
                  </div>`
                : ''}`;
    }

    templateTile() {
        return html` <div
                class="${classMap({
                    'catalog-item-back': true,
                    'has-actions': this.hasCounterSlot || this.hasOneClickSlot,
                })}"
            >
                <slot></slot>
            </div>
            <div
                class="${classMap({
                    'catalog-item': true,
                    'has-actions': this.hasCounterSlot || this.hasOneClickSlot,
                })}"
                ${ref(this._itemTileRef)}
                part="catalog-item"
            >
                <div class="catalog-item-img">
                    <div class="catalog-item-actions">
                        <slot name="compare"></slot>
                    </div>
                    <slot name="img"></slot>
                    <div class="catalog-item-labels">
                        <slot name="tag"></slot>
                    </div>
                </div>
                <div class="catalog-item-info">
                    <div part="catalog-item-prices" class="catalog-item-prices">
                        <span class="catalog-item-price-unit">
                            <slot name="min-price-unit"></slot>
                        </span>
                        <span class="catalog-item-price">
                            <slot name="min-price"></slot>
                        </span>
                        <span class="catalog-item-price catalog-item-price--old">
                            <slot name="min-price-old"></slot>
                        </span>
                    </div>

                    <div class="catalog-item-details">
                        <div class="catalog-item-name" part="catalog-item-name">
                            <slot name="name"></slot>
                        </div>
                        ${this.articleProp
                            ? html`<div class="catalog-item-article" part="catalog-item-article">
                                  <slot name="${this.articleProp}"></slot>
                              </div>`
                            : nothing}
                        ${this.properties.includes(AVAILABLE_COUNT_PROP)
                            ? html`<div class="catalog-item-available" part="catalog-item-available">
                                  <slot name="${AVAILABLE_COUNT_PROP}"></slot>
                              </div>`
                            : nothing}
                    </div>
                </div>
                <div
                    class="${classMap({
                        'catalog-item-counter': true,
                        'has-counter': this.hasCounterSlot,
                        'has-one-click': this.hasOneClickSlot,
                    })}"
                    @slotchange="${this._handleTileActionSlotChange}"
                >
                    <slot name="counter" ${ref(this._tileCounterSlotRef)}></slot>
                    <slot name="one-click" ${ref(this._tileOneClickSlotRef)}></slot>
                </div>
                <div
                    part="catalog-item-property-list"
                    class="${classMap({
                        'catalog-item-property-list': true,
                        'has-content': this.hasPropertyListContent,
                    })}"
                    ${ref(this._itemTilePropertyListRef)}
                >
                    <sotbit-scroll-panel
                        class="catalog-item-property-list-scroll"
                        part="catalog-item-property-list-scroll"
                    >
                        <div class="catalog-item-property-list-scroll__content">
                            ${map(this.properties, (property) => {
                                if (property === this.articleProp || property === AVAILABLE_COUNT_PROP) {
                                    return;
                                }

                                return html`
                                    <slot
                                        name="${property}"
                                        ${ref(this._setPropertySlotRef(property))}
                                        @slotchange="${this._handlePropertyListSlotChange}"
                                    ></slot>
                                `;
                            })}
                        </div>
                    </sotbit-scroll-panel>
                </div>
            </div>`;
    }

    render() {
        return html`${cache(this.variant === CATALOG_VARIANT_TABLE ? this.templateTable() : this.templateTile())}`;
    }
}
